import { BrandProfileField, BrandProfileFieldName, BrandProfilePayload } from 'domains/brand/types';

/** Order for brand profile fields in edit form, strength checklist, and next-step hints. */
export const BRAND_PROFILE_FIELD_DISPLAY_ORDER: BrandProfileFieldName[] = [
    BrandProfileFieldName.OrganizationName,
    BrandProfileFieldName.BrandDescription,
    BrandProfileFieldName.BrandToneKeywords,
    BrandProfileFieldName.SampleTemplateIds,
    BrandProfileFieldName.UseKeywords,
    BrandProfileFieldName.DontUseKeywords,
];

const NEXT_STEP_PRIORITY_ORDER = BRAND_PROFILE_FIELD_DISPLAY_ORDER;

const NEXT_STEP_MESSAGES: Partial<Record<BrandProfileFieldName, string>> = {
    [BrandProfileFieldName.SampleTemplateIds]: 'Add sample writing so AI can learn from real examples.',
    [BrandProfileFieldName.UseKeywords]: 'Add words to use so AI can learn your preferred phrases.',
    [BrandProfileFieldName.DontUseKeywords]: 'Add words to avoid so AI can match your brand voice.',
};

export const CHECKLIST_LABELS: Record<BrandProfileFieldName, string> = {
    [BrandProfileFieldName.OrganizationName]: 'Organization name',
    [BrandProfileFieldName.BrandDescription]: 'Brand description',
    [BrandProfileFieldName.BrandToneKeywords]: 'Tone keywords',
    [BrandProfileFieldName.UseKeywords]: 'Words to use',
    [BrandProfileFieldName.DontUseKeywords]: 'Words to avoid',
    [BrandProfileFieldName.SampleTemplateIds]: 'Sample content',
};

export const isFieldSatisfied = (
    value: string | string[] | number[] | null | undefined,
    fieldName?: string
): boolean => {
    if (value === null || value === undefined) return false;
    if (value === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    if (fieldName === BrandProfileFieldName.BrandDescription && typeof value === 'string') {
        const wordCount = value.trim() ? value.trim().split(/\s+/).filter(Boolean).length : 0;
        return wordCount >= 50;
    }
    return true;
};

export const getFieldValue = (
    field: BrandProfileField,
    formValues?: BrandProfilePayload
): string | string[] | number[] | null | undefined => {
    if (formValues && field.fieldName in formValues) {
        return formValues[field.fieldName];
    }
    return field.currentValue;
};

export const calculatePercentage = (brandProfile: BrandProfileField[], formValues?: BrandProfilePayload): number => {
    const totalWeight = brandProfile.reduce((sum, f) => sum + f.weightage, 0);
    let satisfiedWeight = 0;
    brandProfile.forEach((f) => {
        const value = getFieldValue(f, formValues);
        if (isFieldSatisfied(value, f.fieldName)) satisfiedWeight += f.weightage;
    });
    return totalWeight > 0 ? Math.round((satisfiedWeight / totalWeight) * 100) : 0;
};

const isFieldFilled = (
    fieldName: BrandProfileFieldName,
    brandProfile: BrandProfileField[],
    formValues?: BrandProfilePayload
): boolean => {
    const field = brandProfile.find((f) => f.fieldName === fieldName);
    if (!field) return false;
    return isFieldSatisfied(getFieldValue(field, formValues), field.fieldName);
};

export type BrandLearningStrengthStatusCopy = { status: string; nextStep: string | null };

export const getStatusCopy = (
    brandProfile: BrandProfileField[],
    formValues?: BrandProfilePayload
): BrandLearningStrengthStatusCopy => {
    const orgFilled = isFieldFilled(BrandProfileFieldName.OrganizationName, brandProfile, formValues);
    const brandDescFilled = isFieldFilled(BrandProfileFieldName.BrandDescription, brandProfile, formValues);
    const toneKeywordsFilled = isFieldFilled(BrandProfileFieldName.BrandToneKeywords, brandProfile, formValues);
    const sampleFilled = isFieldFilled(BrandProfileFieldName.SampleTemplateIds, brandProfile, formValues);
    const useKeywordsFilled = isFieldFilled(BrandProfileFieldName.UseKeywords, brandProfile, formValues);
    const dontUseFilled = isFieldFilled(BrandProfileFieldName.DontUseKeywords, brandProfile, formValues);

    const allFieldsFilled =
        orgFilled && brandDescFilled && toneKeywordsFilled && sampleFilled && useKeywordsFilled && dontUseFilled;

    if (allFieldsFilled) {
        return {
            status:
                "Great! AI will avoid these in future messages. You're all set! Save to use your brand voice in future messages.",
            nextStep: null,
        };
    }
    if (orgFilled && brandDescFilled && toneKeywordsFilled) {
        const firstUnsatisfiedFieldName = NEXT_STEP_PRIORITY_ORDER.find((fieldName) => {
            const field = brandProfile.find((f) => f.fieldName === fieldName);
            return field && !isFieldSatisfied(getFieldValue(field, formValues), field.fieldName);
        });
        const nextStep = firstUnsatisfiedFieldName ? NEXT_STEP_MESSAGES[firstUnsatisfiedFieldName] ?? null : null;
        return {
            status: 'Perfect! AI now has a clearer sense of your tone.',
            nextStep,
        };
    }
    if (orgFilled && brandDescFilled) {
        return {
            status: 'Great! This helps AI understand your communication style.',
            nextStep: "Add tone keywords so AI can match your brand's voice.",
        };
    }
    if (orgFilled) {
        return {
            status: 'Nice! AI can now reference your organization correctly.',
            nextStep: 'Add a brand description to teach AI how you communicate.',
        };
    }
    return {
        status:
            "The more information you add, the better the AI can generate messages that match your brand. Let's start with your organization's name.",
        nextStep: null,
    };
};
